import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Sheet,
  Button,
  Alert,
  Input,
  FormControl,
  FormLabel,
  Stack
} from '@mui/joy';
import PhoneIcon from '@mui/icons-material/Phone';
import TelephonyCallerEndpointFields from './TelephonyCallerEndpointFields';
import useTelephonyCallerEndpoints from '../../hooks/useTelephonyCallerEndpoints';

export default function OutboundCallSetup({ open, onClose, onSetup, loading = false, agentHandler }) {
  const [selectedCallerId, setSelectedCallerId] = useState(null);
  const [numberToCall, setNumberToCall] = useState('');
  const [step, setStep] = useState(1); // 1: select caller ID, 2: enter number to call
  const [callerEndpointKind, setCallerEndpointKind] = useState('phone');

  const { phoneNumbers, registrations, loading: loadingEndpoints, error: endpointsError } =
    useTelephonyCallerEndpoints(agentHandler, 'originate', open);

  useEffect(() => {
    if (!open) return;
    setCallerEndpointKind('phone');
    setSelectedCallerId(null);
    setNumberToCall('');
    setStep(1);
  }, [open]);

  const handleCallerIdSelect = (value) => {
    setSelectedCallerId(value);
    setStep(2);
  };

  const handleSetup = () => {
    if (!selectedCallerId || !numberToCall.trim()) {
      return;
    }
    onSetup({
      callerId: selectedCallerId,
      numberToCall: numberToCall.trim(),
      callerKind: callerEndpointKind,
    });
  };

  const handleBack = () => {
    setStep(1);
    setSelectedCallerId(null);
  };

  const handleClose = () => {
    setStep(1);
    setSelectedCallerId(null);
    setNumberToCall('');
    onClose();
  };

  const emptyPhone =
    "No outbound-capable phone numbers for this agent's backend. Enable outbound on a DDI under Numbering.";
  const emptyReg =
    "No outbound-capable registration endpoints. Enable outbound on a registration under Registrations.";

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        aria-labelledby="outbound-call-setup"
        size="md"
        sx={{ minWidth: 400 }}
      >
        <ModalClose />
        <Typography
          id="outbound-call-setup"
          component="h2"
          level="h4"
          textColor="inherit"
          fontWeight="lg"
          mb={1}
        >
          {step === 1 ? 'Select Caller ID' : 'Enter Number to Call'}
        </Typography>

        {step === 1 ? (
          <>
            <Typography level="body-sm" mb={2}>
              Choose an allocated number or SIP registration endpoint to present as caller ID:
            </Typography>

            {endpointsError ? (
              <Alert color="danger" sx={{ mb: 2 }}>
                {endpointsError}
              </Alert>
            ) : null}

            <TelephonyCallerEndpointFields
              purpose="originate"
              callerKind={null}
              kind={callerEndpointKind}
              onKindChange={(v) => {
                setCallerEndpointKind(v);
                setSelectedCallerId(null);
              }}
              phoneNumbers={phoneNumbers}
              registrations={registrations}
              loading={loadingEndpoints}
              selectedPhone={callerEndpointKind === 'phone' ? selectedCallerId : ''}
              onSelectedPhone={handleCallerIdSelect}
              selectedRegistrationId={callerEndpointKind === 'registration' ? selectedCallerId : ''}
              onSelectedRegistrationId={handleCallerIdSelect}
              emptyPhoneHint={emptyPhone}
              emptyRegistrationHint={emptyReg}
            />

          </>
        ) : (
          <>
            <Typography level="body-sm" mb={2}>
              Enter the phone number you want to call:
            </Typography>

            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Selected Caller ID</FormLabel>
                <Input
                  value={selectedCallerId || ''}
                  disabled
                  startDecorator={<PhoneIcon />}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Number to Call</FormLabel>
                <Input
                  value={numberToCall}
                  onChange={(e) => setNumberToCall(e.target.value)}
                  placeholder="+1234567890"
                  startDecorator={<PhoneIcon />}
                  disabled={loading}
                />
              </FormControl>
            </Stack>
          </>
        )}

        <Sheet sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          {step === 2 && (
            <Button variant="plain" color="neutral" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button variant="plain" color="neutral" onClick={handleClose}>
            Cancel
          </Button>
          {step === 2 && (
            <Button
              onClick={handleSetup}
              disabled={loading || !numberToCall.trim()}
            >
              Start Call
            </Button>
          )}
        </Sheet>
      </ModalDialog>
    </Modal>
  );
}
